<script lang="ts" setup>
import { onKeyStroke, useLiveAnnouncer, useLocalStorage } from '@vueuse/core';
import { ToastProvider, TooltipProvider } from 'reka-ui';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useToast } from '@@/packages/shared/components/toast/useToast';
import { useStore } from '@/store/useStore';
import ToastViewport from '@@/packages/shared/components/toast/ToastViewport.vue';
import { useCurrentUser, useCurrentUserPermissions } from '@/modules/current-user/api';
import { useThemePreference, type ThemePreference } from '@@/packages/shared/composables/theme';
import { useCurrentOrg } from '@/modules/current-org/api';
import AppContent from '@/components/layouts/AppContent.vue';
import AppLoadingScreen from '@/components/layouts/AppLoadingScreen.vue';
import { isSwitchingOrg } from '@/modules/auth/state';
import { setUserProperties } from '@/libs/analytics';
import { useCurrentOrgRoles } from '@/modules/roles/api';
import { useTrial } from '@/modules/trial/api';

// Loaded lazily behind the mode check so prod builds drop the MSW graph.
const MswEnabledBanner =
  import.meta.env.MODE === 'prod'
    ? null
    : defineAsyncComponent(() => import('@/components/debug/MswEnabledBanner.vue'));

const { t } = useI18n();
const toast = useToast();

// The delay lets the new page set its title before it's read out. A change to only
// the query or hash, such as a filter, stays on the same page.
const { polite } = useLiveAnnouncer();
useRouter().afterEach((to, from) => {
  if (to.path === from.path) return;
  setTimeout(() => polite(t('components.layout.pageLoaded', { title: document.title })), 500);
});

const store = useStore();
const { user } = useCurrentUser();
const { currentOrg } = useCurrentOrg();
const { trial } = useTrial();

watchEffect(() => {
  if (!user.value || !currentOrg.value) return;
  setUserProperties({
    status: store.state.assessment.status,
    user: user.value,
    org: currentOrg.value,
    trial: trial.value || undefined,
  });
});

// For persisting debug user role, we watch it from App.vue
// userRole is set from DebugMenuContent.vue
const { roles } = useCurrentOrgRoles();
const userRole = useLocalStorage<string | undefined>('debug-user-role', undefined);
const { override } = useCurrentUserPermissions();

watchEffect(() => {
  if (userRole.value) {
    const role = roles.value?.find((r) => r.id === userRole.value);
    if (role) {
      override(role.permissions);
    }
  }
});

watch(
  () => store.state.legacy.api.api_error,
  (error) => {
    if (!error) return;
    // We don't want to show the toast for the tags error - it's handled inline
    if (error !== 'API_ERROR_TAGS') {
      toast.error(t(`errors.${error}`));
    }
    nextTick(() => {
      store.dispatch('legacy/clearGlobalError');
    });
  },
);

const isFontLoaded = ref(false);
provide('isFontLoaded', isFontLoaded);
onMounted(async () => {
  const FONT_STRING = '14px "Material Symbols Outlined"';
  await document.fonts.load(FONT_STRING);
  // We have observed an issue in Chrome 140.0.7339.208 where this value was sometimes set to false, even when the font was loaded
  isFontLoaded.value = document.fonts.check(FONT_STRING);
});

const { theme, themePreference } = useThemePreference();
watchEffect(() => {
  // This must be set on the body, not just #app or a div further down, so that
  // it also applies to teleported stuff like modals
  document.body.classList.toggle('tw-dark', theme.value === 'dark');

  if (window.Intercom) {
    window.Intercom('update', {
      theme_mode: theme.value === 'dark' ? 'dark' : 'light',
    });
  }
});

// This is a dev tool to quickly change the theme
const currentEnv = import.meta.env.MODE;
const themes: ThemePreference[] = ['classic', 'light', 'dark'];

onKeyStroke('T', (k) => {
  if (currentEnv !== 'prod') {
    // this won't work on windows, but our devs are only using macs if you are
    // coming here to make this work on windows, feel free to curse my name.
    if (k.ctrlKey && k.shiftKey) {
      themePreference.value = themes[(themes.indexOf(themePreference.value) + 1) % themes.length];
      // oxlint-disable-next-line no-console
      console.log('changing theme to:', themePreference.value);
    }
  }
});
</script>

<template>
  <div class="h-full">
    <ToastProvider disable-swipe>
      <TooltipProvider :delay-duration="200">
        <div class="flex h-full">
          <AppLoadingScreen
            v-if="isSwitchingOrg"
            :message="$t('components.layout.loadingOrganisation')"
          />
          <AppContent v-else />
          <div id="inline-drawer-target"></div>
        </div>
        <ToastViewport />
        <MswEnabledBanner v-if="MswEnabledBanner" />
      </TooltipProvider>
    </ToastProvider>
  </div>
</template>
